import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import getValue from 'lodash/get';
import { object as objectYup, string as stringYup, ref as refYup, string } from 'yup';
import Swal from 'sweetalert2';
import ReCAPTCHA from 'react-google-recaptcha';
import { getSiteKey } from '../../../lib/utils/common-functions';
import useInitialErrors from '../../../hooks/formik-initial-errors';
import { SocialEnum } from '../../../types/auth';
import { ErrorIcon, Message, SweetAlertTitle, WarningIcon } from '../../../lib/constants/messages';
import { passwordRegExp } from '../../../lib/utils/validation';
import { CandidateInvitationAPIFormType, CandidateInvitationFormType } from '../../../types/CandidateInvitation';
import { candidateInvitationService } from '../../../lib/api/candidateInvitationService';
import { isCaptchaEnabled } from '../../../config/constants';

type Props = {
    ConfirmCandidateInvitationId: string;
    setCandidateInviteSignInData: (key: CandidateInvitationAPIFormType) => void;
};

const initialFormData = {
    email: '',
    tempPassword: '',
    password: '',
    cpassword: '',
    recaptcha: '',
};

const CandidateInvitationSignUp: React.FC<Props> = ({ ConfirmCandidateInvitationId, setCandidateInviteSignInData }) => {
    const [showCpass, setShowCpass] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [showTempPass, setShowTempPass] = useState(false);
    const [loadingSpin, setLoadingSpin] = useState(false);
    const [customPass, setCustomPass] = useState('');
    const [tempPassValue, setTempPassValue] = useState('');
    const [base64PassString, setBase64PassString] = useState('');
    const [tempPasswordBase64, setTempPasswordBase64] = useState('');
    const [renderKey, setRenderKey] = useState(false);
    const [attempts, setAttempts] = useState(3);

    const captchaEnabled = String(isCaptchaEnabled).toLowerCase() === 'true' ? true : false;

    const profileFormData = useMemo(() => {}, []);

    const initialErrors = useInitialErrors(profileFormData, getValidationSchema(captchaEnabled));

    const submitForm = useCallback(
        async (values: CandidateInvitationFormType) => {
            if (!base64PassString && !tempPasswordBase64) {
                return;
            } else {
                const requestData: CandidateInvitationAPIFormType = {
                    email: values.email,
                    invitationId: ConfirmCandidateInvitationId,
                    temporaryPassword: tempPasswordBase64,
                    newPassword: base64PassString,
                    authType: SocialEnum.CustomAuth,
                };

                try {
                    setLoadingSpin(true);

                    const {
                        resultObject: { authenticationSuccess, passwordAttempt },
                    } = await candidateInvitationService.candidateInvitationAndCount(requestData);

                    if (authenticationSuccess) {
                        setCandidateInviteSignInData(requestData);
                        setLoadingSpin(false);
                    } else {
                        setAttempts(passwordAttempt);
                        setLoadingSpin(false);
                    }

                    if (!authenticationSuccess && passwordAttempt === 0) {
                        setLoadingSpin(false);
                        Swal.fire({
                            icon: ErrorIcon,
                            text: Message.AccountBlocked,
                            confirmButtonText: 'OK',
                            allowOutsideClick: false,
                        });
                    }
                } catch (error: any) {
                    setLoadingSpin(false);
                    if (error.response.status === 400) {
                        if (error.response.data.error.message === Message.UserExists) {
                            Swal.fire({
                                title: SweetAlertTitle.Sorry,
                                text: Message.EmailAlreadyRegistered,
                                icon: WarningIcon,
                                confirmButtonText: 'Ok',
                            });
                        }
                    }
                }
            }
        },
        [ConfirmCandidateInvitationId, base64PassString, setCandidateInviteSignInData, tempPasswordBase64]
    );

    useEffect(() => {
        if (customPass) {
            const convertToBase64 = () => {
                const encodedString = btoa(customPass);
                setBase64PassString(encodedString);
            };

            convertToBase64();
        }

        if (tempPassValue.length > 2) {
            const convertToBase64 = () => {
                const encodedString = btoa(tempPassValue.trim());
                setTempPasswordBase64(encodedString);
            };

            convertToBase64();
        }
    }, [customPass, tempPassValue]);

    return (
        <div className="mt-5">
            <Formik
                onSubmit={submitForm}
                enableReinitialize
                initialValues={initialFormData}
                validationSchema={getValidationSchema(captchaEnabled)}
                initialErrors={initialErrors}
            >
                {(formikProps: FormikProps<CandidateInvitationFormType>) => {
                    const { handleSubmit, values, setFieldTouched, setFieldValue, isValid } = formikProps;

                    const siteKey = getSiteKey();

                    return (
                        <form onSubmit={handleSubmit}>
                            <>
                                {loadingSpin && (
                                    <div className="new-spinner p-absolute">
                                        <div>
                                            <span className="spinner-border spinner-border-sm custom-spinner-border" />
                                        </div>
                                        <p className="fs-14 custom-loading-text">Loading</p>
                                    </div>
                                )}

                                <div className="w-100 mb-4" style={{ marginTop: '55px' }}>
                                    <h1 className="fs-32 fw-600 mb-2">Log In</h1>
                                </div>
                                <div
                                    className="alert lt-bg-faq-light-alt alert-dismissible mb-3 p-2 fade show"
                                    role="alert"
                                >
                                    <span className="fs-12 lt-text-dark-blue">
                                        This is a one-time process to create your account.
                                    </span>
                                </div>
                                <div className="mb-3">
                                    <label className="fs-14 fw-400 mb-2">
                                        Email <span className="lt-text-error">*</span>
                                    </label>
                                    <Field name="email">
                                        {(fieldProps: FieldProps) => {
                                            const { field, form } = fieldProps;
                                            const error =
                                                getValue(form.touched, field.name) && getValue(form.errors, field.name);
                                            return (
                                                <>
                                                    <input
                                                        type="email"
                                                        value={field.value}
                                                        onChange={(ev) => {
                                                            setFieldTouched(field.name);
                                                            setFieldValue(field.name, ev.target.value);
                                                            // setCustomEmail(ev.target.value);
                                                        }}
                                                        className="form-control h-44"
                                                        placeholder="Enter Email"
                                                    />
                                                    {typeof error === 'string' && (
                                                        <small className="text-danger fs-12">{error}</small>
                                                    )}
                                                </>
                                            );
                                        }}
                                    </Field>
                                </div>
                                <div className="mb-3">
                                    <label className="fs-14 fw-400 mb-2">
                                        Temporary password <span className="lt-text-error">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <Field name="tempPassword">
                                            {(fieldProps: FieldProps) => {
                                                const { field, form } = fieldProps;
                                                const error =
                                                    getValue(form.touched, field.name) &&
                                                    getValue(form.errors, field.name);
                                                return (
                                                    <>
                                                        <input
                                                            type={`${showTempPass ? 'text' : 'password'}`}
                                                            // value={field.value}
                                                            onChange={(ev) => {
                                                                setFieldTouched(field.name);
                                                                setFieldValue(field.name, ev.target.value);
                                                                if (ev.target.value === '') {
                                                                    setTempPassValue('');
                                                                } else {
                                                                    setTempPassValue(ev.target.value);
                                                                }
                                                            }}
                                                            className="form-control h-44"
                                                            placeholder="Enter temporary password"
                                                            style={{ paddingRight: '2rem' }}
                                                        />

                                                        <i
                                                            onClick={() => setShowTempPass(!showTempPass)}
                                                            className={`bi ${
                                                                showTempPass ? 'bi-eye' : 'bi-eye-slash'
                                                            } password-toggle cursor-pointer`}
                                                        ></i>
                                                        {typeof error === 'string' && (
                                                            <small className="text-danger fs-12">{error}</small>
                                                        )}
                                                    </>
                                                );
                                            }}
                                        </Field>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6 mb-3">
                                        <label className="fs-14 fw-400 mb-2">
                                            New password <span className="lt-text-error">*</span>{' '}
                                            {/* <i className="bi bi-info-circle lt-text-warning" /> */}
                                            <span className="lt-tooltip ms-1">
                                                <span className="lt-tooltiptext lt-tooltiptext--right fw-400 fs-12">
                                                    <div className="fs-14 fw-600 mb-2">Password instructions</div>

                                                    <ul className="lt-text-disabled fs-14 ps-3">
                                                        <li className="mb-2">At least one uppercase character.</li>
                                                        <li className="mb-2">At least one lowercase character.</li>
                                                        <li className="mb-2">At least one special character.</li>
                                                        <li className="mb-2">At least one number.</li>
                                                        <li className="mb-2">
                                                            Password must be in between 8 - 16 characters.
                                                        </li>
                                                        <li>Password should not match with your temporary password.</li>
                                                    </ul>
                                                </span>
                                                <i className="bi bi-info-circle lt-text-warning" />
                                            </span>
                                        </label>{' '}
                                        <div className="position-relative">
                                            <Field name="password">
                                                {(fieldProps: FieldProps) => {
                                                    const { field, form } = fieldProps;
                                                    const error =
                                                        getValue(form.touched, field.name) &&
                                                        getValue(form.errors, field.name);
                                                    return (
                                                        <>
                                                            <input
                                                                type={`${showPass ? 'text' : 'password'}`}
                                                                // value={field.value}
                                                                onChange={(ev) => {
                                                                    setFieldTouched(field.name);
                                                                    setFieldValue(field.name, ev.target.value);
                                                                }}
                                                                className="form-control h-44"
                                                                placeholder="Enter new password"
                                                                style={{ paddingRight: '2rem' }}
                                                                minLength={8}
                                                                maxLength={16}
                                                                formNoValidate
                                                            />

                                                            <i
                                                                onClick={() => setShowPass(!showPass)}
                                                                className={`bi ${
                                                                    showPass ? 'bi-eye' : 'bi-eye-slash'
                                                                } password-toggle cursor-pointer`}
                                                            ></i>
                                                            {typeof error === 'string' && (
                                                                <small className="text-danger fs-12">{error}</small>
                                                            )}
                                                        </>
                                                    );
                                                }}
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="col-6 mb-3">
                                        <label className="fs-14 fw-400 mb-2">
                                            Confirm password <span className="lt-text-error">*</span>
                                        </label>

                                        <Field name="cpassword">
                                            {(fieldProps: FieldProps) => {
                                                const { field, form } = fieldProps;
                                                const error =
                                                    getValue(form.touched, field.name) &&
                                                    getValue(form.errors, field.name);
                                                return (
                                                    <>
                                                        <div className="position-relative">
                                                            <input
                                                                type={`${showCpass ? 'text' : 'password'}`}
                                                                // value={field.value}
                                                                onChange={(ev) => {
                                                                    setFieldTouched(field.name);
                                                                    setFieldValue(field.name, ev.target.value);
                                                                    setCustomPass(ev.target.value);
                                                                }}
                                                                className="form-control h-44"
                                                                placeholder="Enter confirm password"
                                                                style={{ paddingRight: '2rem' }}
                                                                minLength={8}
                                                                maxLength={16}
                                                            />
                                                            <i
                                                                onClick={() => setShowCpass(!showCpass)}
                                                                className={`bi ${
                                                                    showCpass ? 'bi-eye' : 'bi-eye-slash'
                                                                } password-toggle cursor-pointer`}
                                                            ></i>
                                                        </div>
                                                        {typeof error === 'string' && (
                                                            <small className="text-danger fs-12">{error}</small>
                                                        )}
                                                    </>
                                                );
                                            }}
                                        </Field>
                                    </div>
                                </div>
                                {attempts === 0 ? (
                                    <></>
                                ) : (
                                    <div className="mb-3">
                                        <span
                                            className="m-2 fs-12 text-error"
                                            style={{
                                                display: `${attempts === 3 || attempts === 0 ? 'none' : 'block'}`,
                                            }}
                                        >
                                            {attempts === 3
                                                ? ''
                                                : `Incorrect temporary password. ${attempts} attempt(s) left`}
                                        </span>{' '}
                                    </div>
                                )}

                                {captchaEnabled && (
                                    <div className="mb-3">
                                        <Field name="recaptcha" key={renderKey}>
                                            {(fieldProps: FieldProps) => {
                                                const { field, form } = fieldProps;
                                                const error =
                                                    getValue(form.touched, field.name) &&
                                                    getValue(form.errors, field.name);
                                                return (
                                                    <div>
                                                        <ReCAPTCHA
                                                            sitekey={siteKey}
                                                            onChange={(value) => {
                                                                setFieldTouched(field.name);
                                                                setFieldValue(field.name, value);
                                                            }}
                                                        />
                                                        {typeof error === 'string' && (
                                                            <small className="text-danger fs-12">{error}</small>
                                                        )}
                                                    </div>
                                                );
                                            }}
                                        </Field>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <button type="submit" disabled={!isValid} className="btn btn-primary w-100 h-44">
                                        Log In
                                    </button>
                                </div>
                            </>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default CandidateInvitationSignUp;

const getValidationSchema = (captchaEnabled: boolean) =>
    objectYup().shape({
        recaptcha: captchaEnabled ? stringYup().nullable().required('Captcha is required.') : stringYup().nullable(),
        email: stringYup().required('Email is required.'),
        tempPassword: stringYup().required('Temporary password is required.'),
        password: stringYup()
            .required('Password is required.')
            .min(8, 'Password must be in between 8 - 16 characters.')
            .matches(passwordRegExp, 'Password must match the conditions.'),
        cpassword: stringYup()
            .nullable()
            .oneOf([refYup('password'), null], 'Password and Confirm password must match.')
            .required('Confirm Password is required.'),
    });
